import { useState } from "react";
import { Link } from "react-router-dom";
import SetupWizard from "@/components/SetupWizard";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { HandCoins, Warning, UsersThree, ArrowsDownUp, ChartLineUp } from "@phosphor-icons/react";
import { Th, Td, Empty, Tag, Panel, SectionTitle } from "@/components/common";
import { api, money, fmtDate, todayStr, qty, L, num } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import OverduePanel from "@/components/OverduePanel";
import DayKpis from "@/components/DayKpis";
import PendingShipments from "@/components/PendingShipments";
import { HeroHeader, BigCards, AssetsLiabilities } from "@/components/DashboardHero";
import RatesDialog from "@/components/RatesDialog";
import CashflowCard from "@/components/CashflowCard";

function DayMoves({ title, rows, kind, testid, icon, accent }) {
  return (
    <Panel title={title} icon={icon} accent={accent} testid={testid} right={<span className="font-num text-xs text-muted-foreground">{rows.length} kayıt</span>}>
      <div className="max-h-[340px] overflow-auto">
        <table className="w-full">
          <thead><tr>{kind === "acc" ? <><Th>Cari</Th><Th>İşlem</Th><Th right>Borç</Th><Th right>Alacak</Th></> : <><Th>Ürün</Th><Th>İşlem</Th><Th right>Giriş</Th><Th right>Çıkış</Th></>}</tr></thead>
          <tbody>
            {rows.length === 0 && <Empty cols={4} text="Bu gün için hareket yok" />}
            {rows.map((m) => kind === "acc" ? (
              <tr key={m.id} className="even:bg-zinc-50 hover:bg-muted/40">
                <Td><Link to={`/cariler/${m.account_id}`} className="hover:text-primary">{m.account_name}</Link></Td>
                <Td><span className="text-xs text-muted-foreground">{L.accMove[m.type]} · {m.ref_no}</span></Td>
                <Td right>{m.debit ? num(m.debit) : "—"}</Td><Td right>{m.credit ? num(m.credit) : "—"}</Td>
              </tr>
            ) : (
              <tr key={m.id} className="even:bg-zinc-50 hover:bg-muted/40">
                <Td><Link to={`/stok/${m.product_id}`} className="hover:text-primary">{m.product_name}</Link></Td>
                <Td><span className="text-xs text-muted-foreground">{L.stockMove[m.type]} · {m.ref_no}</span></Td>
                <Td right className="text-emerald-700">{m.qty > 0 ? qty(m.qty) : "—"}</Td><Td right className="text-red-600">{m.qty < 0 ? qty(-m.qty) : "—"}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function ListPanel({ title, items, render, testid, icon, accent }) {
  return (
    <Panel title={title} icon={icon} accent={accent} testid={testid}>
      <ul className="divide-y divide-zinc-100">
        {items.length === 0 && <li className="px-5 py-6 text-center text-sm text-muted-foreground">Kayıt yok</li>}
        {items.map(render)}
      </ul>
    </Panel>
  );
}

export default function Dashboard() {
  const [day, setDay] = useState(todayStr());
  const [d] = useLoad(() => api.get("/dashboard", { params: { day } }).then((r) => r.data), [day]);
  const [rates, reloadRates] = useLoad(() => api.get("/rates").then((r) => r.data), []);
  const [ratesOpen, setRatesOpen] = useState(false);
  const chart = (d?.chart || []).map((c) => ({ ...c, label: fmtDate(c.date).slice(0, 5) }));

  return (
    <div>
      {!d ? <div className="label-eyebrow" data-testid="dashboard-loading">Yükleniyor…</div> : (
        <div className="space-y-8">
          <SetupWizard />
          <HeroHeader day={day} rates={rates} today={d.today} onRatesClick={() => setRatesOpen(true)} />
          <RatesDialog open={ratesOpen} onOpenChange={setRatesOpen} rates={rates} onSaved={reloadRates} />
          <BigCards d={d} />
          <AssetsLiabilities d={d} />
          <div className="flex flex-wrap items-center gap-3">
            <SectionTitle sub="Gün seçin">Günün Detayı</SectionTitle>
            <input type="date" value={day} onChange={(e) => setDay(e.target.value || todayStr())} data-testid="dashboard-day-input"
              className="h-9 rounded-[4px] border border-input bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <DayKpis t={d.today} />
          <CashflowCard />
          <div>
            <SectionTitle sub="Vadesi geçmiş açık faturalar">Vade Takibi</SectionTitle>
            <OverduePanel />
          </div>
          <div>
            <SectionTitle sub="Açık siparişlerde kalan miktarlar">Sevk Bekleyenler</SectionTitle>
            <PendingShipments />
          </div>

          <div>
          <SectionTitle sub="Son 14 gün · ₺ bazında">Hareket Grafiği ve Kritik Stok</SectionTitle>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            <Panel title="Son 14 Gün Hareketleri" icon={ChartLineUp} accent="blue" className="lg:col-span-3" testid="dashboard-chart">
              <div className="h-[300px] p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chart} margin={{ left: 8, right: 8 }}>
                    <CartesianGrid stroke="#e4e4e7" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}B` : v)} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 4, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="sales" name="Satış" fill="#1d4ed8" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="purchases" name="Alış" fill="#f59e0b" radius={[2, 2, 0, 0]} />
                    <Line dataKey="collections" name="Tahsilat" stroke="#047857" strokeWidth={2} dot={false} />
                    <Line dataKey="payments" name="Ödeme" stroke="#dc2626" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Panel>
            <ListPanel title="Kritik Stok" icon={Warning} accent="red" testid="dashboard-low-stock" items={d.low_stock} render={(p) => (
              <li key={p.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                <Link to={`/stok/${p.id}`} className="truncate pr-2 hover:text-primary">{p.name}</Link>
                <Tag tone="low">{qty(p.stock)} {p.unit}</Tag>
              </li>
            )} />
          </div>
          </div>

          <div>
          <SectionTitle sub={fmtDate(day)}>Günün Hareketleri</SectionTitle>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <DayMoves title="Cari Hareketleri" icon={UsersThree} accent="blue" rows={d.account_moves} kind="acc" testid="dashboard-account-moves" />
            <DayMoves title="Stok Hareketleri" icon={ArrowsDownUp} accent="green" rows={d.stock_moves} kind="stock" testid="dashboard-stock-moves" />
          </div>
          </div>

          <div>
          <SectionTitle sub="En yüksek bakiyeler">Cari Bakiyeler</SectionTitle>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {[["En Çok Borçlu Cariler", d.top_debtors, "text-emerald-700", "dashboard-top-debtors", "green"],
              ["Borçlu Olduğumuz Cariler", d.top_creditors, "text-red-600", "dashboard-top-creditors", "red"]].map(([title, items, c, id, ac]) => (
              <ListPanel key={id} title={title} icon={HandCoins} accent={ac} testid={id} items={items} render={(a) => (
                <li key={a.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                  <Link to={`/cariler/${a.id}`} className="truncate pr-2 hover:text-primary">{a.name}</Link>
                  <span className={`font-num font-semibold ${c}`}>{money(a.balance)}</span>
                </li>
              )} />
            ))}
          </div>
          </div>
        </div>
      )}
    </div>
  );
}
